<template>
    <pbl-panel>
        <div class="condition-status-header">
            <div class="condition-status-header__first">
                <div class="condition-status-header__first__status">
                    <span class="condition-status-header__first__status-label">
                        {{ t('common.conditionStatus.label') }}
                    </span>
                    <div 
                        v-if="componentId && !isPending && !isError"
                        class="condition-status-header__first__status-value"
                    >
                        <ColorBadge
                            v-if="currentGeneralStatus"
                            :color="currentGeneralStatus.color"
                            :text="t(currentGeneralStatus.labelKey)"
                        />   
                        <ConditionStatusNoActionCategoryModal :component-type="componentType" :is-no-action-set="!!isNoActionSet" :component-sid="componentId" :no-action-component="noActionComponent">
                            <ConditionStatusHeaderNoActionCategoryButton :is-no-action-set="!!isNoActionSet" :no-action-component="noActionComponent" :component-type="componentType" />
                        </ConditionStatusNoActionCategoryModal>
                    </div>
                    <pbl-spinner v-else-if="isPending" />
                    <div
                        v-else-if="isError"
                        class="condition-status-header__first__status-error"
                    >
                        <pbl-icon name="exclamation-triangle" />
                        <span>
                            {{ t('common.failedToLoad') }}
                        </span>
                    </div>
                </div>
                <div class="condition-status-header__first__actions">
                    <div v-if="lastSyncedDate" class="condition-status-header__first__actions__meta">
                        <Info
                            v-tooltip="t('common.conditionStatus.syncInfo.tooltip')"
                            class="condition-status-header__first__actions__meta__icon"
                        />
                        <span> {{ t('common.conditionStatus.lastUpdated') }}: {{ lastSyncedDate }}</span>
                    </div>
                    <pbl-button @click="emit('exportButtonClick')">
                        {{ t('common.export') }}
                    </pbl-button>
                </div>
            </div>
        </div>
    </pbl-panel>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import ColorBadge from '@/components/badge/ColorBadge.vue'
import { computed } from 'vue'
import { StatusTypes } from '@/configuration/status/condition-status'
import { format } from 'date-fns'
import { DATE_TIME_FORMAT } from '@/utils/date-utils'
import ConditionStatusHeaderNoActionCategoryButton from '@/pages/transformers/condition-status/sections/no-action-category/ConditionStatusHeaderNoActionCategoryButton.vue'
import ConditionStatusNoActionCategoryModal from '@/pages/transformers/condition-status/modals/no-action-category/ConditionStatusNoActionCategoryModal.vue'
import { ComponentType } from '@/configuration/component/component-types'
import { NoActionComponentDto } from '@/api/generated/HCA'
import { Info } from 'lucide-vue-next'

const props = defineProps<{
    componentType: ComponentType
    componentId: string | undefined
    generalStatus: number | undefined
    lastSyncedDate: string | null | undefined
    isNoActionSet?: boolean
    noActionComponent?: NoActionComponentDto
    isPending?: boolean
    isError?: boolean
}>()

const emit = defineEmits<{
    'exportButtonClick': []
}>()

const { t } = useI18n()

const currentGeneralStatus = computed(() => {
    if (props.generalStatus === null || props.generalStatus === undefined) {
        return undefined
    }
    return StatusTypes.find((e) => e.value === props.generalStatus)
})

const lastSyncedDate = computed(() => {
    if (props.lastSyncedDate === null || props.lastSyncedDate === undefined) {
        return undefined
    }
    return format(new Date(props.lastSyncedDate), DATE_TIME_FORMAT)
})
</script>

<style scoped lang="scss">
.condition-status-header {
    &__first {
        display: flex;
        justify-content: space-between;

        min-height: 40px;

        &__actions {
            display: flex;
            gap: $spacing-m;
            align-items: center;

            &__meta {
                display: flex;
                gap: $spacing-2xs;

                color: $pbl-text-muted;

                &__icon {
                    height: 16px;
                    width: 16px;
                    
                    color: $pbl-text-muted;
                    
                    &:hover {
                        color: $pbl-primary;
                    }
                }
            }
        }

        &__status {
            display: flex;

            align-items: center;
            gap: $spacing-s;

            pbl-spinner {
                --size: 1.5rem;
                margin-bottom: 1px;
            }

            &-label {
                font-weight: 500;
            }

            &-value {
                display: flex;
                align-items: center;
                gap: $spacing-xl;
            }

            &-error {
                display: flex;

                align-items: center;
                gap: $spacing-2xs;

                color: $pbl-invalid;

                span {
                    margin-top: 1px;
                    font-weight: 500;
                }

                pbl-icon {
                    --size: 1.5rem;

                    color: $pbl-invalid-contrast;
                }
            }
        }
    }

    &__second {
        display: flex;
        justify-content: flex-end;
        gap: $spacing-s;
        margin-top: 10px;
    }
}
</style>
